import React, { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";

const GardenIndex = () => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTag, setSelectedTag] = useState(null);
  const [selectedMaturity, setSelectedMaturity] = useState(null);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    // Initialize selectedTag from URL query parameter
    const tagFromUrl = searchParams.get("tag");
    if (tagFromUrl) {
      setSelectedTag(tagFromUrl);
    }
  }, [searchParams]);

  useEffect(() => {
    const loadGardenIndex = async () => {
      try {
        const response = await fetch("/garden/generated/index.json");
        if (!response.ok) throw new Error("Failed to load garden index");
        const data = await response.json();
        setNotes(Object.values(data.notes || {}));
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    loadGardenIndex();
  }, []);

  // Get all unique tags
  const allTags = [...new Set(notes.flatMap((note) => note.tags))].sort();

  // Filter notes by selected tag and maturity
  const filteredNotes =
    notes.filter((note) => {
      const matchesTag = !selectedTag || note.tags.includes(selectedTag);
      const matchesMaturity = !selectedMaturity || note.maturity === selectedMaturity;
      return matchesTag && matchesMaturity;
    });

  // Sort notes by date first, then maturity
  const maturityOrder = { shrub: 0, sapling: 1, seed: 2 };
  const sortedNotes = [...filteredNotes].sort((a, b) => {
    // Primary sort by tended date (most recently tended first)
    const tendedDiff = new Date(b.tendedDate).getTime() - new Date(a.tendedDate).getTime();
    if (tendedDiff !== 0) return tendedDiff;
    // Secondary sort by planted date (newest first)
    const plantedDiff = new Date(b.plantedDate).getTime() - new Date(a.plantedDate).getTime();
    if (plantedDiff !== 0) return plantedDiff;
    // Tertiary sort by maturity (shrub, sapling, seed)
    const maturityA = maturityOrder[a.maturity] || 3;
    const maturityB = maturityOrder[b.maturity] || 3;
    return maturityA - maturityB;
  });

  const maturityEmoji = {
    seed: "🌱",
    sapling: "🌿",
    shrub: "🌳",
  };

  if (loading) {
    return (
      <div className="garden-page">
        <div className="title">
          <h2 className="heading">garden index. 🌿</h2>
        </div>
        <div className="garden-content">
          <p>Loading your digital garden...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="garden-page">
        <div className="title">
          <h2 className="heading">garden index. 🌿</h2>
        </div>
        <div className="garden-content">
          <p>Error loading garden: {error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="garden-page">
      <div className="title">
        <h2 className="heading">garden index. 🌿</h2>
        <p className="section-subtitle">Browse notes by maturity and topic</p>
      </div>

      <div className="garden-index-content">
        {/* Maturity Filter */}
        <div className="garden-maturity-filter">
          <button
            className={`maturity-filter-btn ${!selectedMaturity ? "active" : ""}`}
            onClick={() => setSelectedMaturity(null)}
          >
            All Maturity
          </button>
          <button
            className={`maturity-filter-btn ${selectedMaturity === "seed" ? "active" : ""}`}
            onClick={() => setSelectedMaturity("seed")}
          >
            🌱 Seeds ({notes.filter((n) => n.maturity === "seed").length})
          </button>
          <button
            className={`maturity-filter-btn ${selectedMaturity === "sapling" ? "active" : ""}`}
            onClick={() => setSelectedMaturity("sapling")}
          >
            🌿 Saplings ({notes.filter((n) => n.maturity === "sapling").length})
          </button>
          <button
            className={`maturity-filter-btn ${selectedMaturity === "shrub" ? "active" : ""}`}
            onClick={() => setSelectedMaturity("shrub")}
          >
            🌳 Shrubs ({notes.filter((n) => n.maturity === "shrub").length})
          </button>
        </div>

        {/* Tag Filter */}
        <div className="garden-tags-filter">
          <button
            className={`tag-filter-btn ${!selectedTag ? "active" : ""}`}
            onClick={() => setSelectedTag(null)}
          >
            All ({notes.length})
          </button>
          {allTags.map((tag) => (
            <button
              key={tag}
              className={`tag-filter-btn ${selectedTag === tag ? "active" : ""}`}
              onClick={() => setSelectedTag(tag)}
            >
              #{tag} ({notes.filter((n) => n.tags.includes(tag)).length})
            </button>
          ))}
        </div>

        {/* Notes Grid */}
        <div className="notes-grid">
          {sortedNotes.map((note) => (
            <Link key={note.slug} to={`/garden/note/${note.slug}`} className="note-card-link">
              <div className="note-card">
                <div className="note-card-header">
                  <h4>{note.title}</h4>
                  <span className="note-maturity">{maturityEmoji[note.maturity]}</span>
                </div>
                {note.image && (
                  <div className="note-card-image">
                    <img src={note.image} alt={note.title} />
                  </div>
                )}
                <p className="note-preview">{note.preview}</p>
                <div className="note-meta">
                  <span className="note-date">
                    {new Date(note.plantedDate).toLocaleDateString()}
                  </span>
                  {note.backlinks.length > 0 && (
                    <span className="note-backlinks">
                      🔗 {note.backlinks.length}
                    </span>
                  )}
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export default GardenIndex;
